import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  getTickets,
  createTicket,
  type SupportTicket,
} from "../../services/support";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import toast from "react-hot-toast";
import { formatDate } from "../../lib/dateFormat";

export default function ClubSupport() {
  const { t } = useTranslation();
  const { activeClubId } = useAuth();

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    subject: "",
    message: "",
    priority: "normal" as "low" | "normal" | "high" | "urgent",
  });

  useEffect(() => {
    if (!activeClubId) return;

    (async () => {
      setLoading(true);
      try {
        const ticketsData = await getTickets();
        setTickets(ticketsData.filter(t => t.club_id === activeClubId));
      } catch (error) {
        console.error("Failed to load tickets:", error);
      }
      setLoading(false);
    })();
  }, [activeClubId]);

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !ticketForm.subject.trim() || !ticketForm.message.trim()) return;

    setSaving(true);
    try {
      await createTicket({
        subject: ticketForm.subject.trim(),
        message: ticketForm.message.trim(),
        priority: ticketForm.priority,
        club_id: activeClubId,
      });
      setTicketForm({ subject: "", message: "", priority: "normal" });
      const ticketsData = await getTickets();
      setTickets(ticketsData.filter(t => t.club_id === activeClubId));
      toast.success(t("support.ticketCreated", "Tichet de suport creat cu succes!"));
    } catch (error) {
      console.error("Failed to create ticket:", error);
      toast.error(t("support.ticketError", "Nu s-a putut crea tichetul de suport"));
    }
    setSaving(false);
  };

  const getTicketStatusColor = (status: string) => {
    switch (status) {
      case "open": return "bg-amber-100 text-amber-700";
      case "in_progress": return "bg-blue-100 text-blue-700";
      case "resolved": return "bg-emerald-100 text-emerald-700";
      case "closed": return "bg-slate-100 text-slate-500";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-700";
      case "high": return "bg-orange-100 text-orange-700";
      case "normal": return "bg-blue-100 text-blue-700";
      case "low": return "bg-slate-100 text-slate-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent"></div>
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="space-y-6">
      <AdminPageHeader
        title={t("admin.support", "Suport")}
        subtitle={t("clubSupport.subtitle", "Deschide un tichet și urmărește solicitările tale.")}
        icon={<ClubNavIcon id="support" className="w-6 h-6" />}
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="sticky p-6 bg-white border shadow-sm rounded-2xl border-slate-200 top-6">
            <h3 className="mb-4 text-lg font-semibold text-slate-800">{t("support.createTicket", "Creează tichet")}</h3>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("support.subject", "Subiect")} *</label>
                <input
                  type="text"
                  required
                  value={ticketForm.subject}
                  onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                  placeholder={t("support.subjectPlaceholder", "Descriere scurtă a problemei")}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("support.priority", "Prioritate")}</label>
                <select
                  value={ticketForm.priority}
                  onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value as "low" | "normal" | "high" | "urgent" })}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="low">{t("support.low", "Mică")}</option>
                  <option value="normal">{t("support.normal", "Normală")}</option>
                  <option value="high">{t("support.high", "Mare")}</option>
                  <option value="urgent">{t("support.urgent", "Urgentă")}</option>
                </select>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-slate-700">{t("support.message", "Mesaj")} *</label>
                <textarea
                  required
                  rows={5}
                  value={ticketForm.message}
                  onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                  placeholder={t("support.messagePlaceholder", "Descrie problema în detaliu...")}
                  className="w-full px-3 py-2 text-sm transition-all bg-white border resize-none border-slate-300 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={saving || !ticketForm.subject || !ticketForm.message}
                className="flex items-center justify-center w-full gap-2 px-6 py-3 font-medium text-white transition-colors bg-indigo-500 hover:bg-indigo-600 rounded-xl disabled:opacity-50"
              >
                {saving ? <div className="w-5 h-5 border-2 border-white rounded-full border-t-transparent animate-spin"></div> : null}
                {t("support.submit", "Trimite tichet")}
              </button>
            </form>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="p-6 bg-white border shadow-sm rounded-2xl border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-slate-800">{t("support.myTickets", "Tichetele mele")}</h3>
              <span className="text-sm text-slate-500">{tickets.length} {t("common.total", "total")}</span>
            </div>
            <div className="space-y-3">
              {tickets.map((ticket) => (
                <div key={ticket.id} className="p-4 transition-colors border bg-slate-50 rounded-xl border-slate-100 hover:border-indigo-300">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-800">{ticket.subject}</h4>
                      <p className="mt-1 text-sm text-slate-500 whitespace-pre-wrap">{ticket.message}</p>
                      {/* Surface the admin's reply inline so the requester
                          sees the answer right where they raised the issue,
                          instead of having to dig through their inbox. */}
                      {(ticket as any).admin_response && (
                        <div className="mt-3 p-3 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg">
                          <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold mb-1">
                            {t("support.adminResponse", "Răspunsul echipei")}
                          </p>
                          {(ticket as any).admin_response_at && (
                            <p className="text-xs text-emerald-700 mb-1">
                              {new Date((ticket as any).admin_response_at).toLocaleString("ro-RO", {
                                day: "2-digit", month: "2-digit", year: "numeric",
                                hour: "2-digit", minute: "2-digit",
                              })}
                            </p>
                          )}
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{(ticket as any).admin_response}</p>
                        </div>
                      )}
                      <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTicketStatusColor(ticket.status)}`}>
                          {ticket.status}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className="text-xs text-slate-400">
                          {formatDate(new Date(ticket.created_at))}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {tickets.length === 0 && (
                <div className="py-12 text-center text-slate-400">{t("support.noTickets", "Niciun tichet de suport încă")}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </ClubAdminLayout>
  );
}
